import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Table, Badge } from 'react-bootstrap';
import api from '../../services/api';
import PaymentModal from '../Payment/PaymentModal';
import ChatButton from '../Chat/ChatButton';
const InitialDashboard = ({ profile, onUpdate }) => {
  const [formData, setFormData] = useState({
    cl_name: '',
    contact_skype: '',
    bank_acc: '',
    company_details: '',
    gw_ip: '',
    gw_protocol: '',
    tech_prefix: '',
    need_invoice: 0,
    id_client_type: '',
    product_id: ''
  });
  const [showPayment, setShowPayment] = useState(false);
  const [protocols, setProtocols] = useState({ voice: [], sms: [] });
  const [clientTypes, setClientTypes] = useState([]);
  const [products, setProducts] = useState({ voice: [], sms: [] });
  const [clientTypeHelp, setClientTypeHelp] = useState({});
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [filteredProtocols, setFilteredProtocols] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const canEdit = profile.status_id === 0;
  const techApproved = profile.tech_approved === 1;
  const finApproved = profile.fin_approved === 1;
  const canEditTech = canEdit && !techApproved;
  const canEditFin = canEdit && !finApproved;
  useEffect(() => {
    loadData();
    loadPayments();
    if (profile) {
      setFormData({
        cl_name: profile.name || '',
        contact_skype: profile.skype || '',
        bank_acc: profile.bank_acc || '',
        company_details: profile.company_details || '',
        gw_ip: profile.gateway_ip || '',
        gw_protocol: profile.gateway_protocol || '',
        tech_prefix: profile.tech_prefix || '',
        need_invoice: profile.need_invoice || 0,
        id_client_type: profile.client_type || '',
        product_id: profile.product_id || ''
      });
      setDocuments(profile.documents || []);
    }
  }, [profile]);
  useEffect(() => {
    if (formData.id_client_type && clientTypes.length > 0) {
      const selectedType = clientTypes.find(t => t.id_client_type === parseInt(formData.id_client_type));
      if (selectedType) {
        const serviceId = selectedType.service_id;
        if (serviceId === 1) {
          setFilteredProtocols(protocols.voice || []);
          setFilteredProducts(products.voice || []);
        } else if (serviceId === 10) {
          setFilteredProtocols(protocols.sms || []);
          setFilteredProducts(products.sms || []);
        } else {
          setFilteredProtocols([]);
          setFilteredProducts([]);
        }
      }
    }
  }, [formData.id_client_type, clientTypes, protocols, products]);
  const loadData = async () => {
    try {
      const [protocolsRes, typesRes, productsRes, helpRes] = await Promise.all([
        api.get('/client/protocols'),
        api.get('/products/client-types'),
        api.get('/products/all'),
        api.get('/client/client-type-help')
      ]);
      setProtocols(protocolsRes.data);
      setClientTypes(typesRes.data);
      setProducts(productsRes.data);
      setClientTypeHelp(helpRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };
  const loadPayments = async () => {
    try {
      const response = await api.get('/client/payments');
      const paymentsData = response.data.payments || response.data || [];
      setPayments(paymentsData);
    } catch (error) {
      console.error('Failed to load payments:', error);
    }
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const fakeEvent = { target: { files: [files[0]], value: '' } };
      handleFileUpload(fakeEvent);
    }
  };
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (documents.length >= 5) {
      setError('Maximum 5 documents allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }
    const formDataFile = new FormData();
    formDataFile.append('file', file);
    try {
      const response = await api.post('/client/upload-document', formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocuments([...documents, response.data.document]);
      setSuccess(`Document uploaded: ${response.data.document.original}`);
      if (e.target) e.target.value = '';
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload document');
    }
  };
  const handleDownloadDocument = async (filename, originalName) => {
    try {
      const response = await api.get(`/client/document/${filename}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to download document');
    }
  };
  const handlePreview = async (filename) => {
    try {
      const response = await api.get(`/client/document/${filename}`, {
        responseType: 'blob'
      });
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      setError('Failed to load document');
    }
  };
  const handleDeleteDocument = async (filename) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/client/document/${filename}`);
      setDocuments(documents.filter(d => d.filename !== filename));
      setSuccess('Document deleted');
    } catch (error) {
      setError('Failed to delete document');
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.cl_name || !formData.id_client_type) {
      setError('Name and client type are required');
      return;
    }
    if (formData.need_invoice === 1 && !formData.bank_acc) {
      setError('Bank account information is required when invoice is needed');
      return;
    }
    if (!formData.gw_protocol || !formData.product_id) {
      setError('Protocol and Product are required');
      return;
    }
    setLoading(true);
    try {
      await api.put('/client/profile', formData);
      setSuccess('Profile updated successfully');
      if (onUpdate) onUpdate();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  const getStatusDescription = () => {
    const statusMap = {
      0: 'Registration in progress - please complete form',
      1: 'Pending admin review',
      2: 'Approved - awaiting payment',
      3: 'Active client'
    };
    return statusMap[profile.status_id] || 'Unknown status';
  };
  const getFieldHelp = (field) => {
    const helps = {
      cl_name: 'Your company or personal name',
      gw_ip: 'IP address of your gateway',
      gw_protocol: 'VoIP/SMS protocol used by your equipment',
      tech_prefix: 'Technical prefix for routing (optional)/SysID (optional, will be assigned if not provided)',
      contact_skype: 'Your Chat ID for quick contact, we use WhatsApp, Telegram, MeetMe (optional)',
      bank_acc: 'Bank account details for invoicing (Mandatory if you are a company)',
      product_id: 'Select the service package you want to use',
      need_invoice: 'Check if you need official invoice for payment (Mandatory if you are a company)'
    };
    return helps[field] || '';
  };
  const handleSubmitForReview = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.cl_name || !formData.id_client_type) {
      setError('Name and client type are required');
      return;
    }
    if (formData.need_invoice === 1) {
      if (!formData.bank_acc) {
        setError('Bank account information is required when invoice is needed');
        return;
      }
      if (!formData.company_details) {
        setError('Company details are required when invoice is needed');
        return;
      }
    }
    if (!formData.gw_protocol || !formData.product_id) {
      setError('Protocol and Product are required');
      return;
    }
    if (!window.confirm('Are you sure you want to submit your registration for review? You will not be able to edit it after submission.')) {
      return;
    }
    setLoading(true);
    try {
      await api.put('/client/submit-for-review', formData);
      setSuccess('Registration submitted for review! Please wait for admin confirmation.');
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.response?.data?.message || 'Submit failed');
    } finally {
      setLoading(false);
    }
  };
  const selectedClientType = clientTypes.find(t => t.id_client_type === parseInt(formData.id_client_type));
  const helpText = selectedClientType ? clientTypeHelp[selectedClientType.client_type_name] : null;
  const hasCompletedPayments = payments.some(p => p.status === 'completed');
  const allApproved = techApproved && finApproved;
  const getHeaderMessage = () => {
    if (allApproved && hasCompletedPayments) {
      return '✅ All stages completed! Your account will be activated soon. Wait for the activation notification via email.';
    }
    if (allApproved && !hasCompletedPayments) {
      return '⏳ Waiting for payment to complete activation.';
    }
    return null;
  };
  return (
    <Container fluid className="mt-4">
      <Card className="shadow-sm">
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              Complete Your Registration - Status: {getStatusDescription()}
            </h4>
            <div className="d-flex gap-2">
              {techApproved && (
                <Badge bg="success" className="p-2">
                  ✓ Technical Approved
                </Badge>
              )}
              {finApproved && (
                <Badge bg="success" className="p-2">
                  ✓ Financial Approved
                </Badge>
              )}
            </div>
          </div>
          {getHeaderMessage() && (
            <Alert variant="info" className="mt-2 mb-0">
              {getHeaderMessage()}
            </Alert>
          )}
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company/Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="cl_name"
                    value={formData.cl_name}
                    onChange={handleChange}
                    disabled={!canEditFin}
                    required
                  />
                  <Form.Text className="text-muted">{getFieldHelp('cl_name')}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Email *{' '}
                    {profile.email_verified === 1 ? (
                      <Badge bg="success">✓ Verified</Badge>
                    ) : (
                      <Badge bg="danger">✗ Not Verified</Badge>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={profile.email}
                    disabled
                    readOnly
                  />
                  <Form.Text className="text-muted">Email cannot be changed</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Client Type *</Form.Label>
                  <Form.Select
                    name="id_client_type"
                    value={formData.id_client_type}
                    onChange={handleChange}
                    disabled={!canEditTech}
                    required
                  >
                    <option value="">Select Type</option>
                    {clientTypes.map(type => (
                      <option key={type.id_client_type} value={type.id_client_type}>
                        {type.client_type_name}
                      </option>
                    ))}
                  </Form.Select>
                  {helpText && (
                    <Form.Text className="text-muted">
                      <small dangerouslySetInnerHTML={{ __html: helpText }} />
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Product (optional)</Form.Label>
                  <Form.Select
                    name="product_id"
                    value={formData.product_id}
                    onChange={handleChange}
                    disabled={!canEdit || !formData.id_client_type}
                  >
                    <option value="">Select Product</option>
                    {filteredProducts.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">{getFieldHelp('product_id')}</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gateway IP</Form.Label>
                  <Form.Control
                    type="text"
                    name="gw_ip"
                    value={formData.gw_ip}
                    onChange={handleChange}
                    disabled={!canEditTech}
                    placeholder="0.0.0.0"
                  />
                  <Form.Text className="text-muted">{getFieldHelp('gw_ip')}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Protocol</Form.Label>
                  <Form.Select
                    name="gw_protocol"
                    value={formData.gw_protocol}
                    onChange={handleChange}
                    disabled={!canEditTech || !formData.id_client_type}
                  >
                    <option value="">Select Protocol</option>
                    {filteredProtocols.map(protocol => (
                      <option key={protocol.id} value={protocol.id}>
                        {protocol.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">{getFieldHelp('gw_protocol')}</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tech Prefix/SysID</Form.Label>
                  <Form.Control
                    type="text"
                    name="tech_prefix"
                    value={formData.tech_prefix}
                    onChange={handleChange}
                    disabled={!canEditTech}
                    placeholder="Optional"
                  />
                  <Form.Text className="text-muted">{getFieldHelp('tech_prefix')}</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Chat</Form.Label>
                  <Form.Control
                    type="text"
                    name="contact_skype"
                    value={formData.contact_skype}
                    onChange={handleChange}
                    disabled={!canEdit}
                    placeholder="WhatsApp/Skype was good/Telegram"
                  />
                  <Form.Text className="text-muted">{getFieldHelp('contact_skype')}</Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="I need invoice for company payment"
                name="need_invoice"
                checked={formData.need_invoice === 1}
                onChange={handleChange}
                disabled={!canEditFin}
              />
              <Form.Text className="text-muted">{getFieldHelp('need_invoice')}</Form.Text>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Bank Account Information {formData.need_invoice === 1 && '*'}</Form.Label>
                  <Form.Control
                    as={formData.need_invoice === 1 ? 'textarea' : 'input'}
                    rows={formData.need_invoice === 1 ? 4 : 1}
                    name="bank_acc"
                    value={formData.bank_acc}
                    onChange={handleChange}
                    disabled={!canEditFin}
                    required={formData.need_invoice === 1}
                  />
                  <Form.Text className="text-muted">{getFieldHelp('bank_acc')}</Form.Text>
                </Form.Group>
              </Col>
              {formData.need_invoice === 1 && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Details *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="company_details"
                      value={formData.company_details}
                      onChange={handleChange}
                      disabled={!canEditFin}
                      placeholder="Company name, Registration number, VAT number, Address"
                      required
                    />
                    <Form.Text className="text-muted">{getFieldHelp('company_details')}</Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Card className="mb-4">
              <Card.Header>
                <strong>📎 Documents Upload</strong>
              </Card.Header>
              <Card.Body>
                {documents.length > 0 && (
                  <Table striped bordered hover size="sm" className="mb-3">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Upload Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc, index) => (
                        <tr key={index}>
                          <td>{doc.original}</td>
                          <td>{new Date(doc.uploaded_at).toLocaleString()}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => handlePreview(doc.filename)}
                            >
                              👁️ View
                            </Button>
                            {canEdit && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteDocument(doc.filename)}
                              >
                                🗑️ Delete
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
                {canEdit && (
                  <div
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('fileInput').click()}
                    style={{
                      border: isDragging ? '3px dashed #0d6efd' : '2px dashed #ccc',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center',
                      backgroundColor: isDragging ? '#e7f3ff' : '#f8f9fa',
                      cursor: documents.length >= 5 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <p className="mb-2">
                      {isDragging ? '📂 Drop file here' : '📎 Drag & drop file here or click to browse'}
                    </p>
                    <Form.Control
                      type="file"
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.jpg,.png"
                      disabled={documents.length >= 5}
                      style={{ display: 'none' }}
                      id="fileInput"
                    />
                  </div>
                )}
                <Form.Text className="text-muted">
                  Upload documents (PDF, DOC, JPG, PNG). Max 5 files, 5MB each. In some cases, we may request certain documents for the identification of a person or a company.
                </Form.Text>
              </Card.Body>
            </Card>
            <div className="d-flex justify-content-between">
              {canEdit && (
                <>
                  <Button type="submit" variant="primary" size="lg" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button variant="success" size="lg" onClick={handleSubmitForReview} disabled={loading}>
                    {loading ? 'Submitting...' : '✓ Complete Registration'}
                  </Button>
                </>
              )}
              <Button variant="success" size="lg" onClick={() => setShowPayment(true)}>
                💰 Top Up Balance
              </Button>
            </div>
          </Form>
          <PaymentModal
            show={showPayment}
            onHide={() => {
              setShowPayment(false);
              loadPayments();
            }}
            onSuccess={() => {
              loadPayments();
            }}
          />
          {payments.length > 0 && (
            <div className="mt-4">
              <h5>Payment History</h5>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Transaction ID</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(payment => (
                    <tr key={payment.id}>
                      <td>{new Date(payment.date).toLocaleString()}</td>
                      <td>{payment.amount} {payment.currency_code}</td>
                      <td>{payment.method}</td>
                      <td>
                        <Badge bg={payment.status === 'completed' ? 'success' : 'warning'}>
                          {payment.status}
                        </Badge>
                      </td>
                      <td>{payment.transaction_id || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      <ChatButton />
    </Container>
  );
};
export default InitialDashboard;